import { useEffect, useState } from "react";
import { Button, Empty, Input, List, Modal, Space, Switch, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined, SaveOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import { dateFormat } from "../../core/utils/date-utils";

/**
 * Çekim notları — satır açılır panelindeki "Notlar" sekmesi.
 *
 * Görünürlük sunucuda (§2.4): kendi notları (gizli/açık farketmez) + başkalarının AÇIK notları.
 * Düzenleme/silme yalnız yazarında; buton `canEdit` bayrağına göre çizilir.
 */
export default function StudyNotesPanel({ studyId }) {
    const { t } = useTranslation();

    const [notes, setNotes] = useState([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [text, setText] = useState('')
    const [isPublic, setIsPublic] = useState(false)
    const [editingId, setEditingId] = useState(null)

    const load = async () => {
        setLoading(true)
        try {
            const res = await ApiService.StudyNote.GetByStudy(studyId);
            if (res.success)
                setNotes(res.data || [])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [studyId]);

    const reset = () => {
        setEditingId(null)
        setText('')
        setIsPublic(false)
    }

    const save = async () => {
        if (!text.trim())
            return;

        setSaving(true)
        try {
            const res = await ApiService.StudyNote.Save({
                id: editingId,
                studyId,
                note: text.trim(),
                isPublic,
            });

            if (res.success) {
                reset()
                await load()
            }
        } finally {
            setSaving(false)
        }
    }

    const startEdit = (note) => {
        setEditingId(note.id)
        setText(note.note)
        setIsPublic(note.isPublic)
    }

    const remove = (note) => {
        Modal.confirm({
            title: t('Delete Note'),
            content: t('This note will be deleted.'),
            okText: t('Delete'),
            okButtonProps: { danger: true },
            cancelText: t('Cancel'),
            onOk: async () => {
                const res = await ApiService.StudyNote.Delete({ id: note.id });
                if (res.success) {
                    if (editingId === note.id)
                        reset()
                    await load()
                }
            }
        });
    }

    return (
        <div>
            <Space direction={'vertical'} style={{ width: '100%', marginBottom: 12 }}>
                <Input.TextArea
                    rows={3}
                    maxLength={2000}
                    showCount
                    placeholder={editingId ? t('Edit note') : t('Write a note about this study')}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                />
                <Space>
                    <Tooltip title={isPublic
                        ? t('Public: everyone who can see this study reads it')
                        : t('Private: only you can read it')}>
                        <Space>
                            <Switch
                                size={'small'}
                                checked={isPublic}
                                onChange={setIsPublic}
                                checkedChildren={<EyeOutlined />}
                                unCheckedChildren={<EyeInvisibleOutlined />}
                            />
                            <span>{isPublic ? t('Public') : t('Private')}</span>
                        </Space>
                    </Tooltip>
                    <Button
                        type={'primary'}
                        size={'small'}
                        icon={<SaveOutlined />}
                        loading={saving}
                        disabled={!text.trim()}
                        onClick={save}
                    >
                        {editingId ? t('Save') : t('Add Note')}
                    </Button>
                    {editingId && (
                        <Button size={'small'} onClick={reset}>
                            {t('Cancel')}
                        </Button>
                    )}
                </Space>
            </Space>

            <List
                size={'small'}
                loading={loading}
                dataSource={notes}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No note yet')} /> }}
                renderItem={(note) => (
                    <List.Item
                        actions={note.canEdit ? [
                            <Button size={'small'} type={'text'} icon={<EditOutlined />} onClick={() => startEdit(note)} />,
                            <Button size={'small'} type={'text'} danger icon={<DeleteOutlined />} onClick={() => remove(note)} />,
                        ] : []}
                    >
                        <List.Item.Meta
                            title={(
                                <Space size={4}>
                                    <span>{note.userName || '-'}</span>
                                    <Tag color={note.isPublic ? 'green' : 'default'}>
                                        {note.isPublic ? t('Public') : t('Private')}
                                    </Tag>
                                    <Typography.Text type={'secondary'} style={{ fontSize: 11 }}>
                                        {dateFormat(note.updatedAt || note.createdAt)}
                                    </Typography.Text>
                                </Space>
                            )}
                            description={<span style={{ whiteSpace: 'pre-wrap', color: '#333' }}>{note.note}</span>}
                        />
                    </List.Item>
                )}
            />
        </div>
    )
}
